<template>
  <div
    class="bg-white border border-[#E2E8F0] rounded-xl shadow-sm min-h-[116px]">
    <!-- Header: AI Badge + Comment Button -->
    <div
      class="flex items-center px-4 py-2 justify-between border-b border-[#E9EEF7] pb-2">
      <!-- Right: Badge -->
      <div
        v-if="!props.isTeacherReview || loading"
        class="flex items-center justify-end gap-1 h-6 px-2 py-0.5 pl-3 rounded-md bg-[#F7F9FD]">
        <AiCrossIcon class="w-4 h-4" alt="AI" />
        <span
          class="text-[#495465] text-right font-simplerBold text-sm leading-[18px]">
          {{ getLocalizedText('ai_feedback') }}
        </span>
      </div>

      <!-- Teacher Review Badge -->
      <div
        v-else
        class="flex items-center justify-end gap-2 h-6 px-2 py-0.5 pl-3 rounded-md bg-[#F7F9FD]">
        <img
          v-if="props.teacherAvatar"
          :src="props.teacherAvatar"
          class="w-4 h-4 rounded-full object-cover"
          alt="Teacher Avatar"/>
        <div
          v-else
          class="w-4 h-4 rounded-full bg-[#CBD5E1] flex items-center justify-center">
          <span class="text-[#64748B] text-xs font-bold">
            {{ props.teacherName?.charAt(0) || 'T' }}
          </span>
        </div>
        <span
          class="text-[#495465] text-right font-simplerBold text-sm leading-[18px]">
          {{ props.teacherName }}
        </span>
        <span
          class="text-[#495465] text-right font-simplerBold text-sm leading-[18px]" style="direction: rtl;">
          {{ formattedReviewDate }}
        </span>
      </div>

      <BaseTooltip v-if="!props.isTeacherReview && props.submissionId">
        <template #trigger>
          <button
            id="comment-btn"
            :disabled="props.loading"
            class="flex items-center gap-0.5 h-6 px-2 py-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed border border-[#D7DFEA] rounded-md bg-white"
            @click="showCommentDialog = true">
            <MessageSquareWarning class="w-4 h-4" />
          </button>
        </template>
        <template #content>{{ getLocalizedText('i_have_comment') }}</template>
      </BaseTooltip>
    </div>

    <div class="px-4 py-3 pb-5">
      <!-- AI Feedback Title -->
      <div
        v-if="!props.isTeacherReview"
        class="flex items-center gap-2 text-[#14532D] font-bold text-sm pb-2">
        <!-- Loading Spinner (ChatGPT style) -->
        <div
          v-if="props.loading"
          class="w-5 h-5 bg-black rounded-full animate-pulse"></div>
        <!-- Check Circle for completed feedback -->
        <StateSuccess v-else-if="level === 'correct'" class="text-green-600" />
        <!-- V-X Circle for partially correct feedback -->
        <StateHalf v-else-if="level === 'partially_correct'" />
        <!-- X Circle for incorrect feedback -->
        <StateFailed v-else />
        <span
          :class="{
            'text-green-600': level === 'correct' && !props.loading,
            'text-red-500':
              (level === 'incorrect' || level === 'failed') && !props.loading,
            'text-[#F0920F]': level === 'partially_correct' && !props.loading,
            'text-[#1E232A]': props.loading,
          }">
          {{
            props.loading
              ? ''
              : props.isTeacherReview
                ? 'Teacher Review'
                : getPerformanceTitle(level)
          }}
        </span>
      </div>

      <!-- Feedback Content -->
      <div
        v-if="!props.loading"
        class="text-[#1E232A] text-start font-simpler text-sm leading-[18px] min-h-[54px]">
        {{ reviewNote }}
      </div>

      <!-- Loading Content -->
      <div
        v-if="props.loading"
        class="text-[#0F172A] text-[15px] leading-relaxed">
        <div class="animate-pulse">
          <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div class="h-4 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Comment Dialog -->
  <PVDialog
    v-model:visible="showCommentDialog"
    :modal="true"
    :closable="true"
    :dismissable-mask="true"
    :dir="props.dir || 'rtl'"
    :style="{
      width: '500px',
      height: '420px',
      direction: props.dir || 'rtl',
      borderRadius: '12px',
    }"
    :pt="{
      header: {
        class: ['h-[38px] relative'],
      },
      headeractions: {
        class: [
          '!flex',
          '!justify-end',
          '!align-end',
          'p-4',
          (props.dir || 'rtl') === 'rtl' ? '!order-first' : '!order-last',
        ],
      },
      closeIcon: {
        class: ['w-4 h-4'],
      },
      content: {
        class: ['!p-[24px]'],
      },
    }">
    <!-- Content -->
    <div :dir="props.dir || 'rtl'">
      <div class="text-[#1E232A] font-bold text-md pb-4">
        {{ getLocalizedText('feedback_comment_title') }}
      </div>
      <div>
        <!-- Explanation -->
        <div
          class="text-[#1E232A] font-simplerPro text-sm font-normal leading-[18px] rounded-xl"
          :class="{
            'text-left': (props.dir || 'rtl') === 'ltr',
            'text-right': (props.dir || 'rtl') === 'rtl',
          }">
          {{ getLocalizedText('ai_feedback_explanation') }}
        </div>

        <!-- Question -->
        <p
          class="text-[#1E232A] font-simplerPro text-sm font-semibold leading-[18px] mt-4"
          :class="{
            'text-left': (props.dir || 'rtl') === 'ltr',
            'text-right': (props.dir || 'rtl') === 'rtl',
          }">
          {{ getLocalizedText('feedback_issue_question') }}
        </p>

        <!-- Textarea -->
        <textarea
          v-model="studentComment"
          :placeholder="getLocalizedText('comment_placeholder')"
          rows="4"
          :dir="props.dir || 'rtl'"
          :class="{
            'text-left': (props.dir || 'rtl') === 'ltr',
            'text-right': (props.dir || 'rtl') === 'rtl',
          }"
          class="w-full p-4 border border-[#CBD5E1] mt-3 rounded-xl text-sm resize-none bg-white placeholder:text-[#94A3B8] focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition"/>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div :dir="props.dir || 'rtl'" class="flex justify-end gap-4 mt-4">
        <PVButton
          :label="getLocalizedText('reset_cancel')"
          class="!bg-white !text-[#334155] hover:!bg-[#F1F5F9] !border-0 !outline-none focus:!outline-none focus:!ring-0"
          @click="showCommentDialog = false"/>

        <BaseButton
          class="h-[34px] flex justify-center"
          :disabled="!studentComment.trim() || props.loading"
          :loading="props.loading"
          :color="!studentComment.trim() || props.loading ? 'gray' : 'blue'"
          @click="submitComment">
          <span class="font-simplerBold text-sm">
            {{ getLocalizedText('submit_comment') }}
          </span>
        </BaseButton>
      </div>
    </template>
  </PVDialog>

  <!-- Success Dialog -->
  <PVDialog
    v-model:visible="showSuccessDialog"
    modal
    :closable="false"
    :pt="{
      header: {
        class: ['h-[38px] relative hidden'],
      },
      headeractions: {
        class: ['absolute top-2 left-2 z-10'],
      },
    }"
    class="w-[460px] relative"
    :style="{ textAlign: 'center' }">
    <div class="flex flex-col items-center gap-3 py-4 relative">
      <!-- Custom X button positioned 20px from top and left -->
      <button
        class="absolute top-0 left-0 p-2 text-gray-400 hover:text-gray-600 transition-colors"
        @click="showSuccessDialog = false">
        <X class="w-4 h-4" />
      </button>

      <AiStudy class="" alt="AI study" />
      <div
        class="text-[#1E232A] text-center font-simplerPro text-xl font-bold leading-6">
        תודה על המשוב!
      </div>
      <p
        class="text-[#1E232A] text-center font-simplerPro text-base font-normal leading-5">
        הערתך התקבלה ותשמש לשיפור המשובים בעתיד.
      </p>
    </div>
  </PVDialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import PVButton from 'primevue/button';
import PVDialog from 'primevue/dialog';
import { MessageSquareWarning, X } from 'lucide-vue-next';
import { useReveal } from '@/apps/slideshow/useReveal';
import { BaseTooltip } from '@/components';
import AiCrossIcon from '@/assets/ai-cross.svg?component';
import AiStudy from '@/assets/ai-study.svg?component';
import StateSuccess from '@/assets/state-success.svg?component';
import StateHalf from '@/assets/state-half.svg?component';
import StateFailed from '@/assets/state-failed.svg?component';

import BaseButton from '@/components/Button/BaseButton.vue';
import { timeAgo, submitFeedbackComment } from '@amit/composables';

const { getLocalizedText } = useReveal();

const props = defineProps<{
  level: string | undefined;
  reviewNote: string | undefined;
  submissionId: string | undefined;
  loading?: boolean;
  isTeacherReview: boolean;
  teacherName?: string;
  teacherAvatar?: string;
  reviewDate?: string;
  dir?: 'ltr' | 'rtl';
}>();

const showCommentDialog = ref(false);
const showSuccessDialog = ref(false);
const studentComment = ref('');

// Use timeAgo function from packages instead of custom computed property
const formattedReviewDate = computed(() => {
  if (!props.reviewDate) return '';
  return timeAgo(props.reviewDate);
});

// This function maps level to UI title
const getPerformanceTitle = (label: string | undefined) => {
  switch (label) {
    case 'correct':
      return getLocalizedText('ai_label_correct') || 'ביצוע תקין';
    case 'incorrect':
      return getLocalizedText('ai_label_incorrect') || 'ביצוע לא תקין';
    case 'partially_correct':
      return getLocalizedText('ai_label_partial') || 'ביצוע חלקי';
    case 'failed':
      return getLocalizedText('ai_label_failed') || 'ביצוע לא תקין';
    default:
      return label || '';
  }
};

const submitComment = async () => {
  if (!studentComment.value.trim() || props.loading) return;

  try {
    // Import the HTTP service
    // Send the comment to the server
    if (!props.submissionId) {
      throw new Error('Submission ID is required');
    }
    const response = await submitFeedbackComment(
      props.submissionId,
      studentComment.value,
    );

    // Check if the response is 204 (success)
    if (response.data) {
      // Close comment dialog and clear comment
      showCommentDialog.value = false;
      studentComment.value = '';

      // Show success dialog
      showSuccessDialog.value = true;
    }
  } catch (error) {
    console.error('Error submitting comment:', error);
  }
};
</script>
